import type { Component } from "replo-runtime";
import type {
  AIStreamingOperation,
  AITriggeringFeature,
  BuildAssistantParams,
} from "schemas/generated/ai";
import type { SchemaReploShopifyProduct } from "schemas/generated/product";

import * as React from "react";

import * as coreActions from "@v1/actions/core-actions";
import { selectActiveCanvas } from "@v1/components/canvas/canvas-reducer";
import { useAiTemplateMutation } from "@v1/reducers/ai-reducer";
import {
  selectComponentDataMapping,
  selectDraftComponent,
  selectEditorMode,
  selectProjectId,
  selectRootComponent,
  selectStreamingUpdateActions,
  selectStreamingUpdateComponentIdsSelected,
  selectStreamingUpdateId,
  selectStreamingUpdateIsStreaming,
  setEditorMode,
  setStreamingUpdate,
} from "@v1/reducers/core-reducer";
import { setSharedState } from "@v1/reducers/paint-reducer";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@v1/store/store";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { forEachComponentAndDescendants } from "replo-runtime";
import { getSectionForComponentId } from "replo-runtime/shared/utils/component";
import * as z from "zod";

type TemplateStreamingParams = {
  type: "template";
  userPrompt: string;
  contextComponent?: Component;
  nonce: number;
  sectionType?: string;
  selectedProduct?: SchemaReploShopifyProduct | null;
  components?: Component[];
  systemPrompt?: string;
  initialPrompt?: string;
  triggeringFeature?: AITriggeringFeature;
};

type MenuTriggerSource =
  | "page-builder"
  | "pill"
  | "previewMenu"
  | "shortcut"
  | "rightClick"
  | "onboarding";

type AIStatus =
  | "preGeneration"
  | "generationInitialized"
  | "generating"
  | "finishedGenerating"
  | "autoApplyingChanges";

interface V1AIStreamingContextType {
  isAIPreLoading: boolean;
  setIsAIPreLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isBuildAssistantMinimized: boolean;
  setIsBuildAssistantMinimized: (newValue: boolean) => void;
  isGenerating: boolean;
  generationMode: AIStreamingOperation | null;
  generationSource: MenuTriggerSource | null;
  initiateParallelGeneration: {
    (params: TemplateStreamingParams, onFinish?: () => void): void;
  };
  abort: () => void;
  isFinished: boolean;
  applyChanges: () => void;
  discardChanges: () => void;
  revert: () => void;
  status: AIStatus;
  completionPercentage?: number;
  isParallelGenerating: boolean;
}

const V1AIStreamingContext = React.createContext<
  V1AIStreamingContextType | undefined
>(undefined);
V1AIStreamingContext.displayName = "V1AIStreamingContext";

export const useV1AIStreaming = (): V1AIStreamingContextType => {
  const context = React.useContext(V1AIStreamingContext);
  if (context === undefined) {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(
      "useV1AIStreaming must be used within a V1AIStreamingProvider",
    );
  }
  return context;
};

export const V1AIStreamingProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const dispatch = useEditorDispatch();
  const store = useEditorStore();

  const isStreaming = useEditorSelector(selectStreamingUpdateIsStreaming);
  const isStreamingFinished = isStreaming === false;

  const [isBuildAssistantMinimized, setIsBuildAssistantMinimized] =
    useLocalStorageState("replo.build-assistant.minimized", false, {
      schema: z.boolean().nullish(),
    });

  const [isAIPreLoading, setIsAIPreLoading] = React.useState(false);

  const [generationMode, setGenerationMode] =
    React.useState<AIStreamingOperation | null>(null);

  const [generationSource, setGenerationSource] =
    React.useState<MenuTriggerSource | null>(null);

  const shouldAutoApplyChanges = generationSource === "onboarding";

  const [triggerAiTemplateMutation] = useAiTemplateMutation();

  const [componentOrderMap, setComponentOrderMap] = React.useState<
    Map<string, number> | undefined
  >();

  const abortRef = React.useRef<(() => void) | null>(null);

  const editorMode = useEditorSelector(selectEditorMode);
  const actions = useEditorSelector(selectStreamingUpdateActions);

  const status: AIStatus = React.useMemo(() => {
    if (editorMode === "aiGeneration") {
      if (isStreaming === false) {
        return shouldAutoApplyChanges
          ? "autoApplyingChanges"
          : "finishedGenerating";
      } else if (actions?.length) {
        return "generating";
      }
      return "generationInitialized";
    }
    return "preGeneration";
  }, [editorMode, isStreaming, actions?.length, shouldAutoApplyChanges]);

  const openAllCollapsibles = useOpenAllCollapsibles();

  const [isParallelGenerating, setIsParallelGenerating] = React.useState(false);

  const initiateParallelGeneration = React.useCallback(
    (params: TemplateStreamingParams, onFinish?: () => void) => {
      setIsParallelGenerating(true);

      const {
        components,
        userPrompt,
        nonce,
        selectedProduct,
        triggeringFeature,
        contextComponent,
      } = params;

      // NOTE (Gabe 2024-08-01): If another generation has already been initiated,
      // this function is a no-op.
      if (status !== "preGeneration") {
        return;
      }

      setGenerationSource("page-builder");
      const rootComponent = selectRootComponent(store.getState());
      setComponentOrderMap(buildComponentOrderMap(rootComponent ?? null));

      const projectId = selectProjectId(store.getState());

      if (!components || !projectId) {
        console.error("No Components or project id");
        return;
      }
      setCompletionPercentage(0);

      dispatch(setEditorMode("aiGeneration"));
      setGenerationMode(params.type);
      openAllCollapsibles();

      const componentsToGenerate: BuildAssistantParams["componentsToGenerate"] =
        [];

      const contextComponentMapping: Record<string, Component> = {};

      for (const component of components) {
        const sectionType = component.name;
        const section = getSectionForComponentId(rootComponent, component.id);

        if (section) {
          contextComponentMapping[component.id] = section;
        }

        componentsToGenerate.push({
          nonce,
          sectionType,
          component,
          userPrompt,
          elementId: component.id,
        });
      }

      const wrappedOnFinish = () => {
        setIsParallelGenerating(false);
        onFinish?.();
      };

      const { abort } = triggerAiTemplateMutation({
        componentsToGenerate,
        contextComponentMapping,
        // NOTE (Noah, 2026-07-20): BuildAssistantParams.selectedProduct is now
        // `.nullish()`, whose generated type is `X | undefined` (no null), so
        // coerce this optionally-null value to undefined. Runtime accepts both.
        selectedProduct: selectedProduct ?? undefined,
        projectId,
        contextComponent,
        triggeringFeature,
        onFinish: wrappedOnFinish,
        generationType: "parallel",
      });
      abortRef.current = abort;
    },
    [openAllCollapsibles, dispatch, status, store, triggerAiTemplateMutation],
  );

  const apply = React.useCallback(() => {
    const actions = selectStreamingUpdateActions(store.getState());
    const streamingUpdateId = selectStreamingUpdateId(store.getState());
    if (!actions || !streamingUpdateId) {
      return;
    }

    dispatch(
      coreActions.applyComponentAction({
        type: "applyCompositeAction",
        value: actions,
        activeCanvas: selectActiveCanvas(store.getState()),
      }),
    );

    dispatch(setEditorMode("edit"));

    if (generationSource !== "page-builder") {
      const componentIdsSelected = selectStreamingUpdateComponentIdsSelected(
        store.getState(),
      );
      dispatch(
        setStreamingUpdate({ isStreaming: false, componentIdsSelected }),
      );

      dispatch(setStreamingUpdate(null));
    }
  }, [store, dispatch, generationSource]);

  const discard = React.useCallback(() => {
    const streamingUpdateId = selectStreamingUpdateId(store.getState());
    if (!streamingUpdateId) {
      return;
    }

    const componentIdsSelected = selectStreamingUpdateComponentIdsSelected(
      store.getState(),
    );

    dispatch(setStreamingUpdate({ isStreaming: false, componentIdsSelected }));
    setTimeout(() => {
      dispatch(setStreamingUpdate(null));
    }, 300);
    dispatch(setEditorMode("edit"));
  }, [dispatch, store]);

  const revert = React.useCallback(() => {
    const componentIdsSelected = selectStreamingUpdateComponentIdsSelected(
      store.getState(),
    );
    dispatch(setStreamingUpdate({ isStreaming: false, componentIdsSelected }));
  }, [dispatch, store]);

  const abort = () => {
    abortRef.current?.();
    setIsAIPreLoading(false);
  };

  const [completionPercentage, setCompletionPercentage] =
    React.useState<number>(0);

  // NOTE (Gabe 2024-07-26): Because there isn't always a 1:1 mapping between
  // (easily countable) components and actions, we instead use the position in
  // the component tree of the latest action as a proxy for completion
  // percentage.
  const latestActionCompletionPercentage = React.useMemo(() => {
    const latestAction = actions?.[actions.length - 1];

    const numComponents = componentOrderMap?.size;
    const lastActionComponentPosition =
      latestAction?.componentId &&
      componentOrderMap?.get(latestAction.componentId);
    if (numComponents && lastActionComponentPosition) {
      return Math.round((lastActionComponentPosition / numComponents) * 100);
    }
    return 0;
  }, [actions, componentOrderMap]);

  // NOTE (Gabe 2024-08-07): Ensure that completion percentage is always
  // increasing (until it gets reset to 0).
  React.useEffect(() => {
    if (latestActionCompletionPercentage > 0) {
      setCompletionPercentage((val) => {
        return Math.max(val, latestActionCompletionPercentage);
      });
    }
  }, [latestActionCompletionPercentage]);

  // Note (Evan, 2024-10-07): This effect is responsible for actually auto-applying changes
  React.useEffect(() => {
    if (status === "autoApplyingChanges") {
      setComponentOrderMap(undefined);
      apply();
    }
  }, [status, apply]);

  return (
    <V1AIStreamingContext.Provider
      value={{
        isBuildAssistantMinimized: isBuildAssistantMinimized ?? false,
        setIsBuildAssistantMinimized,
        isGenerating:
          status === "generating" || status === "generationInitialized",
        generationMode,
        generationSource,
        isAIPreLoading,
        setIsAIPreLoading,
        initiateParallelGeneration,
        abort,
        isFinished: isStreamingFinished,
        applyChanges: () => {
          setComponentOrderMap(undefined);
          apply();
        },
        discardChanges: () => {
          setComponentOrderMap(undefined);
          setIsBuildAssistantMinimized(false);
          discard();
        },
        revert,
        status,
        completionPercentage,
        isParallelGenerating,
      }}
    >
      {children}
    </V1AIStreamingContext.Provider>
  );
};

function buildComponentOrderMap(
  component: Component | null,
): Map<string, number> {
  const map = new Map<string, number>();
  let count = 0;
  forEachComponentAndDescendants(component, (c) => {
    map.set(c.id, count++);
  });
  return map;
}

const useOpenAllCollapsibles = () => {
  const dispatch = useEditorDispatch();
  const store = useEditorStore();
  return React.useCallback(() => {
    const draftComponent = selectDraftComponent(store.getState());
    if (!draftComponent) {
      return;
    }
    const componentDataMapping = selectComponentDataMapping(store.getState());
    const accordionsToOpen: { [accordionId: string]: string[] } = {};
    const componentIdsToOpen: Set<string> = new Set();
    forEachComponentAndDescendants(draftComponent, (component) => {
      if (component.type === "collapsibleV2") {
        const ancestorComponentData =
          componentDataMapping[component.id]?.ancestorComponentData;
        // Note (Evan, 2024-10-16): The shared state that opens/closes collapsibles ~in accordions~
        // is different, so we have to check this and set state accordingly (accordion-gly?)
        const accordionId = ancestorComponentData?.find(
          ([_, type]) => type === "accordionBlock",
        )?.[0];

        if (accordionId) {
          accordionsToOpen[accordionId] = [
            ...(accordionsToOpen[accordionId] ?? []),
            component.id,
          ];
        } else {
          componentIdsToOpen.add(component.id);
        }
      }
    });
    // Note (Evan, 2024-10-18): No need to batch() these on React 18
    Object.entries(accordionsToOpen).forEach(([accordionId, componentIds]) => {
      dispatch(
        setSharedState({
          key: `${accordionId}.accordionOpenItems`,
          value: componentIds,
        }),
      );
    });
    componentIdsToOpen.forEach((componentId) => {
      dispatch(setSharedState({ key: `${componentId}.isOpen`, value: true }));
    });
  }, [dispatch, store]);
};
